import { UtilityTargetSelector } from "@/shared/selectors/UtilityTargetSelector";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import {
	HelmetDexteel,
	TimeService,
	getMomentTz,
	useHasPermission,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { DashboardOutlined, TableChartOutlined } from "@mui/icons-material";
import {
	Box,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { AtAGlance } from "./components/AtAGlance";
import { ConsumptionChart } from "./components/ConsumptionChart";
import { ConsumptionControls } from "./components/ConsumptionControls";
import {
	ConsumptionFilterBar,
	ConsumptionUtilityTabs,
} from "./components/ConsumptionFilterBar";
import { ConsumptionReport } from "./components/ConsumptionReport";
import {
	ConsumptionError,
	ConsumptionMessage,
	ConsumptionSkeletons,
	WipState,
} from "./components/ConsumptionStates";
import { useUtilityConsumption } from "./hooks/useUtilityConsumption";
import { useUtilityConsumptionMeters } from "./hooks/useUtilityConsumptionMeters";
import { useUtilityTargetSelection } from "./hooks/useUtilityTargetSelection";
import {
	UTILITY_CONSUMPTION_MODE,
	UTILITY_CONSUMPTION_UTILITY,
	UTILITY_CONSUMPTION_VIEW,
	type UtilityConsumptionMode,
	type UtilityConsumptionUtility,
	type UtilityConsumptionView,
} from "./models/UtilityConsumption";
import {
	getCurrentUtilityConsumptionPeriod,
	getDefaultUtilityConsumptionReportRange,
	getUtilityConsumptionPeriod,
} from "./utils/calendar";
import { CONSUMPTION_CAPTION_SX } from "./utils/tokens";

const moment = getMomentTz();

const formatRelativeTime = (value: Date, now: Date) => {
	const elapsedMinutes = Math.floor((now.getTime() - value.getTime()) / 60_000);
	if (elapsedMinutes < 1) return "just now";
	if (elapsedMinutes < 60) return `${elapsedMinutes} min ago`;
	if (elapsedMinutes < 1440) return `${Math.floor(elapsedMinutes / 60)} h ago`;
	return `${Math.floor(elapsedMinutes / 1440)} d ago`;
};

const ConsumptionPageReady = ({ plantTimeZone }: { plantTimeZone: string }) => {
	const hasPermissionTo = useHasPermission();
	const canSelectArea = hasPermissionTo("ui.mill.consumption.area.select");
	const { targetId: selectedTargetId, setTargetId: setSelectedTargetId } =
		useUtilityTargetSelection();

	const [meterIds, setMeterIds] = useQueryState(
		"meters",
		parseAsArrayOf(parseAsString),
	);
	const [utility, setUtility] = useState<UtilityConsumptionUtility>(
		UTILITY_CONSUMPTION_UTILITY.WATER,
	);
	const [view, setView] = useState<UtilityConsumptionView>(
		UTILITY_CONSUMPTION_VIEW.DASHBOARD,
	);
	const [reportRange, setReportRange] = useState(() =>
		getDefaultUtilityConsumptionReportRange(plantTimeZone),
	);
	const [mode, setMode] = useState<UtilityConsumptionMode>(
		UTILITY_CONSUMPTION_MODE.DAY,
	);
	const [referenceDate, setReferenceDate] = useState(
		() =>
			getCurrentUtilityConsumptionPeriod(
				UTILITY_CONSUMPTION_MODE.DAY,
				plantTimeZone,
			).startUtc,
	);

	const period = getUtilityConsumptionPeriod(
		mode,
		referenceDate,
		plantTimeZone,
	);

	const areaSelector = canSelectArea ? (
		<UtilityTargetSelector
			targetId={selectedTargetId}
			onTargetChange={(targetId) => setSelectedTargetId(targetId)}
		/>
	) : undefined;

	const {
		data: meters,
		error: meterError,
		isError: isMeterError,
		isLoading: isMeterLoading,
		refetch: refetchMeters,
		resolvedExternalMeterIds,
		isEmptySelection,
	} = useUtilityConsumptionMeters({
		targetId: selectedTargetId,
		externalMeterIds: meterIds,
	});

	const { data, error, isError, isLoading, refetch } = useUtilityConsumption({
		targetId: selectedTargetId,
		enabled: view === UTILITY_CONSUMPTION_VIEW.DASHBOARD && !isEmptySelection,
		utility,
		externalMeterIds: resolvedExternalMeterIds,
		mode,
		period,
	});

	const showCost = selectedTargetId !== null;
	const isWorkInProgress = utility !== UTILITY_CONSUMPTION_UTILITY.WATER;
	const setPeriodMode = (nextMode: UtilityConsumptionMode) => {
		setMode(nextMode);
		setReferenceDate(
			getCurrentUtilityConsumptionPeriod(nextMode, plantTimeZone).startUtc,
		);
	};
	const filterByMeter = (meterId: string) =>
		setMeterIds((current) =>
			current !== null && current.length === 1 && current[0] === meterId
				? null
				: [meterId],
		);

	const dataThroughUtc = data?.summary.DataThroughUtc ?? null;
	const dataThrough = dataThroughUtc
		? `Data through ${moment
				.utc(dataThroughUtc)
				.tz(plantTimeZone)
				.format(
					"MMM D, YYYY HH:mm",
				)} · ${formatRelativeTime(dataThroughUtc, new Date())}`
		: null;

	useEffect(() => {
		if (meterIds === null || meters === undefined) return;
		const configuredIds = new Set(meters.map((meter) => meter.ExternalMeterId));
		const validIds = meterIds.filter((meterId) => configuredIds.has(meterId));
		if (validIds.length === meterIds.length) return;
		setMeterIds(validIds.length === 0 ? null : validIds);
	}, [meterIds, meters]);

	return (
		<>
			<HelmetDexteel title="Utility Report" />
			<Box
				sx={{
					bgcolor: pageBgColor,
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					height: { lg: "calc(100vh - 90px)" },
					minHeight: { lg: "calc(100vh - 90px)" },
					p: { xs: 1, md: 2 },
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 1,
						mb: 1,
					}}
				>
					<Typography
						component="h1"
						sx={{ fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.25 }}
					>
						Utility Report
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 0.75,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						{dataThrough && (
							<Box sx={{ alignItems: "center", display: "flex", gap: 0.25 }}>
								<Typography component="div" sx={CONSUMPTION_CAPTION_SX}>
									{dataThrough}
								</Typography>
							</Box>
						)}
						<ToggleButtonGroup
							exclusive
							size="small"
							value={view}
							aria-label="Consumption view"
							onChange={(_, value: UtilityConsumptionView | null) =>
								value && setView(value)
							}
							sx={{
								"& .MuiToggleButton-root": { px: 1.25, textTransform: "none" },
								"& .Mui-selected": {
									bgcolor: "primary.main",
									color: "primary.contrastText",
									"&:hover": { bgcolor: "primary.dark" },
								},
							}}
						>
							<Tooltip title="Dashboard view: cards and charts">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.DASHBOARD}
									aria-label="Dashboard view"
								>
									<DashboardOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Dashboard
								</ToggleButton>
							</Tooltip>
							<Tooltip title="Report view: detailed table">
								<ToggleButton
									value={UTILITY_CONSUMPTION_VIEW.REPORT}
									aria-label="Report view"
								>
									<TableChartOutlined fontSize="small" sx={{ mr: 0.5 }} />
									Report
								</ToggleButton>
							</Tooltip>
						</ToggleButtonGroup>
					</Box>
				</Box>
				<ConsumptionUtilityTabs
					utility={utility}
					onUtilityChange={setUtility}
				/>
				{isMeterError ? (
					<ConsumptionError
						message={
							meterError instanceof Error
								? meterError.message
								: "Unexpected response."
						}
						onRetry={() => refetchMeters()}
					/>
				) : !canSelectArea ? (
					<>
						<ConsumptionFilterBar
							meters={[]}
							isMeterLoading={false}
							selectedMeterIds={null}
							areaSelector={areaSelector}
							onMeterChange={setMeterIds}
						/>
						<ConsumptionMessage
							title="Area not supported"
							detail="This area has no utility target of its own, so its consumption is not tracked."
						/>
					</>
				) : view === UTILITY_CONSUMPTION_VIEW.DASHBOARD ? (
					<>
						<ConsumptionFilterBar
							meters={meters ?? []}
							isMeterLoading={isMeterLoading}
							selectedMeterIds={meterIds}
							areaSelector={areaSelector}
							onMeterChange={setMeterIds}
						/>
						{isWorkInProgress ? (
							<WipState utility={utility} />
						) : isEmptySelection ? (
							<ConsumptionMessage
								title="No meters selected"
								detail="Tick at least one meter in the filter to see consumption."
							/>
						) : (
							<Box
								sx={{
									display: "flex",
									flex: 1,
									flexDirection: "column",
									gap: 1,
									minHeight: 0,
								}}
							>
								{isError && (
									<ConsumptionError
										message={
											error instanceof Error
												? error.message
												: "Unexpected response."
										}
										onRetry={() => refetch()}
									/>
								)}
								{isLoading && data === undefined && <ConsumptionSkeletons />}
								{data && meters?.length === 0 && (
									<ConsumptionMessage
										title="No meters configured"
										detail="No active water meters are assigned to this mill."
									/>
								)}
								{data &&
									(meters?.length ?? 0) > 0 &&
									!data.summary.IsAvailable && (
										<ConsumptionMessage
											title={
												meterIds !== null
													? "Selected meters have no readings"
													: "No reporting meters"
											}
										/>
									)}
								{data?.summary.IsAvailable && (
									<>
										<AtAGlance
											summary={data.summary}
											millRolling={data.millRolling}
											meterGaps={data.meterGaps}
											showCost={showCost}
											buckets={data.miniBuckets}
											plantTimeZone={plantTimeZone}
										/>
										<ConsumptionChart
											buckets={data.mainBuckets}
											meters={meters ?? []}
											summary={data.summary}
											mode={mode}
											period={period}
											plantTimeZone={plantTimeZone}
											selectedMeterIds={resolvedExternalMeterIds}
											onMeterFilter={filterByMeter}
											controls={
												<ConsumptionControls
													mode={mode}
													referenceDate={period.startUtc}
													plantTimeZone={plantTimeZone}
													onModeChange={setPeriodMode}
													onReferenceDateChange={setReferenceDate}
												/>
											}
										/>
									</>
								)}
							</Box>
						)}
					</>
				) : (
					<ConsumptionReport
						targetId={selectedTargetId}
						utility={utility}
						meters={meters ?? []}
						isMeterLoading={isMeterLoading}
						selectedMeterIds={meterIds}
						resolvedMeterIds={resolvedExternalMeterIds}
						areMetersLoaded={meters !== undefined}
						isEmptySelection={isEmptySelection}
						range={reportRange}
						plantTimeZone={plantTimeZone}
						areaSelector={areaSelector}
						showCost={showCost}
						onMeterChange={setMeterIds}
						onRangeChange={setReportRange}
					/>
				)}
			</Box>
		</>
	);
};

export const ConsumptionPage = () => {
	const { state, isLoading } = useUTLSettingsContext();
	const configuredTimeZone = state.settings.TimeZone;

	if (isLoading) {
		return <ConsumptionSkeletons />;
	}

	if (
		typeof configuredTimeZone !== "string" ||
		configuredTimeZone.trim().length === 0
	) {
		return <ConsumptionSkeletons />;
	}

	const plantTimeZone = TimeService.getInstance().getServerTimeZone().trim();

	if (moment.tz.zone(plantTimeZone) === null) {
		return <ConsumptionSkeletons />;
	}

	return (
		<ConsumptionPageReady key={plantTimeZone} plantTimeZone={plantTimeZone} />
	);
};
